import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trackPurchaseOnce } from "../lib/ads";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, Flame, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const [pack, setPack] = useState(false); const [kind, setKind] = useState(""); const [deptId, setDeptId] = useState(null); const [unlock, setUnlock] = useState(false); const [started, setStarted] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("failed"); return; }

    const poll = async () => {
      if (polls.current >= MAX_POLLS) { setStatus("failed"); return; }
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          trackPurchaseOnce(sessionId, typeof data.amount_cents === "number" ? data.amount_cents / 100 : undefined, (data.currency || "usd").toUpperCase()); // once per session; guarded against refresh
          setPack(!!data.one_time); setKind(data.lookup_key || ""); setDeptId(data.department_id || null); setUnlock(!!data.unlock_existing); setStarted(!!data.research_started);
          setStatus("paid");
          await refresh();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
        setTimeout(poll, 2000);
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [params, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 tactical-grid px-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-primary animate-spin mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-6">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-6" data-testid="payment-success-title">{pack ? (kind?.startsWith("dept_research") ? (unlock ? "Department unlocked!" : started ? "Research started!" : "Department Access purchased!") : kind === "dept_search_1" ? "Searches added!" : "10 interviews added!") : "You're Premium!"} <Flame className="inline w-6 h-6 text-primary" /></h1>
            <p className="text-slate-500 mt-2">{pack ? (kind?.startsWith("dept_research") ? (unlock ? "This department is permanently yours — full profile, flashcards and quizzes are open now." : started ? "We're researching the department now (about a minute). It will be permanently yours when it finishes — head back to watch progress." : "Your Department Access is saved on your account. Head back to the department and press Start research — if research is paused right now, the credit waits for you.") : kind === "dept_search_1" ? "Your extra department searches are on your account — they carry over until you use them." : "10 interviews added. Purchased interviews never expire and carry over between billing periods and membership changes.") : "Your Premium is active. Time to run some mock interviews."}</p>
            <div className="flex flex-col gap-3 mt-8">
              <Button onClick={() => navigate(kind?.startsWith("dept_research") ? (deptId ? `/study/departments/${deptId}` : "/study/departments") : kind === "dept_search_1" ? "/study/departments" : "/interview")} className="bg-primary hover:bg-primary/90 h-12" data-testid="success-interview-button">
                {kind?.startsWith("dept_research") ? "Back to the department" : kind === "dept_search_1" ? "Open Department Research" : "Back to the Mock Oral Board"} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="h-12" data-testid="success-dashboard-button">Go to Dashboard</Button>
            </div>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="w-9 h-9 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-6">Payment not confirmed</h1>
            <p className="text-slate-500 mt-2">We couldn't confirm your payment yet. If you were charged, it may take a moment to reflect.</p>
            <div className="flex flex-col gap-3 mt-8">
              <Button onClick={() => navigate("/pricing")} className="bg-primary hover:bg-primary/90 h-12" data-testid="success-retry-button">Back to Pricing</Button>
              <Button onClick={() => navigate("/dashboard")} variant="outline" className="h-12">Go to Dashboard</Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
